import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";
import { useField, useForm } from "react-final-form";
import { useLocation } from "react-router-dom";
import {
  getKybWidgetLink,
  saveDocumentDataForDocument,
  sendKybAllInvites,
  sendKybInvite,
} from "../../network";
import { useLinkedVerificationRequests } from "../../hooks/useLinkedVerificationRequests";
import { TLinkedVerificationRequest } from "../../models";
import {
  EntityRepresentativeReviewRow,
  EntityRepresentativesPerson,
  getEntityRepresentativeRows,
  splitEntityRepresentativeRows,
} from "../../utils/entityRepresentatives";
import { parseLinkedVrs } from "../../utils/linkedVrs";
import { getEditablePrimaryDocumentData } from "../../utils/pendingDocument";
import type { DocumentTypeRendererProps } from "./DocumentTypeRenderer";
import { AccordionShell } from "../StepAccordion";
import { VerificationStatusBadge } from "../VerificationStatusBadge";
import { AddPersonDialog } from "./AddPersonDialog";

const ACTION_BUTTON_SX = {
  minWidth: 0,
  padding: "0 4px",
  fontSize: "0.75rem",
};

const ENTITY_REPRESENTATIVES_DATA_ID = "EntityRepresentatives";
const PEOPLE_FIELD_NAME = "people";

// Reads the raw persons array stored in the document data without going
// through the parser, so an Add/Remove save preserves byte-equal field
// values on existing rows (Decision 7).
function getRawPeoplePayload(
  entry: { data?: unknown } | undefined,
): EntityRepresentativesPerson[] {
  if (!entry) return [];
  const data = entry.data as { people?: unknown } | undefined;
  if (data && Array.isArray(data.people)) {
    return data.people as EntityRepresentativesPerson[];
  }
  return [];
}

function getLinkedVrStatus(
  row: EntityRepresentativeReviewRow,
  linksByVrId: Map<string, TLinkedVerificationRequest>,
) {
  if (!row.childVrId) {
    return undefined;
  }

  return linksByVrId.get(String(row.childVrId));
}

function getRoleChips(row: EntityRepresentativeReviewRow) {
  const chips = [];

  if (row.representative) {
    chips.push(
      <Chip
        key="representative"
        label="Representative"
        size="small"
        variant="outlined"
      />,
    );
  }

  if (row.ubo) {
    chips.push(<Chip key="ubo" label="UBO" size="small" variant="outlined" />);
  }

  return chips;
}

function hasNewlyRemovedPerson(
  values: Record<string, unknown>,
  initialPeople: EntityRepresentativesPerson[],
) {
  const currentPeople = values[PEOPLE_FIELD_NAME];
  if (!Array.isArray(currentPeople)) {
    return false;
  }

  const initialRemovedById = new Map(
    initialPeople
      .filter((person) => person.id)
      .map((person) => [String(person.id), person.removed === true]),
  );

  return (currentPeople as EntityRepresentativesPerson[]).some((person) => {
    if (!person.id || person.removed !== true) {
      return false;
    }

    return initialRemovedById.get(String(person.id)) !== true;
  });
}

export const EntityRepresentativesRenderer = memo(
  (props: DocumentTypeRendererProps) => {
    const {
      document,
      requestId,
      title,
      companyRole,
      canEdit,
      comments,
      addComment,
      statusesConfig,
      onRefreshDocuments,
      defaultExpanded,
    } = props;
    const [, , refreshLinks] = useLinkedVerificationRequests(requestId);

    const loadedMainEntry = useMemo(
      () =>
        document.data.find(
          (d) =>
            d.type === "binary" && d.dataId === document.document.documentType,
        ),
      [document.data, document.document.documentType],
    );
    const mainEntry = useMemo(
      () =>
        loadedMainEntry ??
        (canEdit ? getEditablePrimaryDocumentData(document) : undefined),
      [canEdit, document, loadedMainEntry],
    );

    // Seed the RFF main payload with the raw people[] so Add/Remove can stage
    // changes locally and the shared accordion Save button posts the merged
    // people array through saveDocumentData like the widget does.
    const initialPeople = useMemo(
      () => getRawPeoplePayload(mainEntry),
      [mainEntry],
    );
    const initialValues = useMemo(
      () => ({ [PEOPLE_FIELD_NAME]: initialPeople }),
      [initialPeople],
    );

    const canEditPayload = canEdit && !!mainEntry;
    const onSavePayload = useCallback(
      async (values: Record<string, unknown>) => {
        const effectiveDocument = await saveDocumentDataForDocument(
          document.document,
          requestId,
          ENTITY_REPRESENTATIVES_DATA_ID,
          values,
        );
        return effectiveDocument.documentId;
      },
      [document.document, requestId],
    );
    const onSaveSuccess = useCallback(async () => {
      await Promise.all([
        Promise.resolve(onRefreshDocuments?.()),
        refreshLinks(),
      ]);
    }, [onRefreshDocuments, refreshLinks]);
    const payloadSaveConfirmation = useMemo(
      () => ({
        shouldConfirm: (values: Record<string, unknown>) =>
          hasNewlyRemovedPerson(values, initialPeople),
        title: "Save removed associated parties?",
        description:
          "Removed associated parties will be disconnected from this entity. Linked verification request statuses will not change, and the relationship will stay visible as a removed link.",
        confirmText: "Save",
        declineText: "Cancel",
      }),
      [initialPeople],
    );

    return (
      <AccordionShell
        title={title}
        document={document}
        companyRole={companyRole}
        comments={comments}
        addComment={addComment}
        initialValues={initialValues}
        canEditPayload={canEditPayload}
        requiresPayloadSave={mainEntry?.id === ""}
        onSavePayload={canEditPayload ? onSavePayload : undefined}
        payloadSaveConfirmation={payloadSaveConfirmation}
        onSaveSuccess={onSaveSuccess}
        defaultExpanded={defaultExpanded}
      >
        <EntityRepresentativesBody
          document={document}
          requestId={requestId}
          canEdit={canEdit}
          statusesConfig={statusesConfig}
        />
      </AccordionShell>
    );
  },
);

type BodyProps = {
  document: DocumentTypeRendererProps["document"];
  requestId: string;
  canEdit: boolean;
  statusesConfig: DocumentTypeRendererProps["statusesConfig"];
};

function EntityRepresentativesBody(props: BodyProps) {
  const { document, requestId, canEdit, statusesConfig } = props;
  const form = useForm();
  const peopleField = useField<EntityRepresentativesPerson[]>(
    PEOPLE_FIELD_NAME,
    { subscription: { value: true } },
  );
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showRemoved, setShowRemoved] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [links] = useLinkedVerificationRequests(requestId);
  const location = useLocation();

  const linkedVrs = useMemo(
    () => parseLinkedVrs(document.data),
    [document.data],
  );

  const peopleValue = peopleField.input.value;
  // Local Add/Remove operations stage edits in the RFF people[] draft. The
  // accordion's Save bar reads form values and persists them when clicked,
  // so the row list must reflect the current draft, not the loaded mainEntry.
  const personRows = useMemo(
    () =>
      getEntityRepresentativeRows(
        peopleAsBinaryEntry(peopleValue),
        linkedVrs,
      ),
    [linkedVrs, peopleValue],
  );

  const { activeRows, removedRows } = useMemo(
    () => splitEntityRepresentativeRows(personRows),
    [personRows],
  );

  const linksByVrId = useMemo(
    () =>
      new Map(
        links.map((link) => [String(link.verificationRequestId), link]),
      ),
    [links],
  );

  const sendEmailEligibleRows = useMemo(
    () =>
      activeRows.filter(
        (row) => !!row.childVrId && row.childVrHasEmail,
      ),
    [activeRows],
  );

  const shouldShowSendAll = sendEmailEligibleRows.length > 1;

  const withAction = useCallback(
    async (actionId: string, callback: () => Promise<void>) => {
      setBusyAction(actionId);
      setErrorMessage("");
      try {
        await callback();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Action failed";
        setErrorMessage(message);
      } finally {
        setBusyAction(null);
      }
    },
    [],
  );

  const handleCopyLink = useCallback(
    async (childVrId: number) => {
      await withAction(`copy-${childVrId}`, async () => {
        const data = await getKybWidgetLink(childVrId);
        if (!navigator?.clipboard?.writeText) {
          throw new Error("Clipboard is unavailable");
        }
        await navigator.clipboard.writeText(data.link);
      });
    },
    [withAction],
  );

  const handleSendInvite = useCallback(
    async (childVrId: number) => {
      await withAction(`invite-${childVrId}`, async () => {
        await sendKybInvite(childVrId);
      });
    },
    [withAction],
  );

  const handleSendAll = useCallback(async () => {
    await withAction("invite-all", async () => {
      await sendKybAllInvites(requestId);
    });
  }, [requestId, withAction]);

  const readCurrentPeople = useCallback(
    (): EntityRepresentativesPerson[] => {
      const values = (form.getState().values ?? {}) as Record<string, unknown>;
      const list = values[PEOPLE_FIELD_NAME];
      return Array.isArray(list) ? (list as EntityRepresentativesPerson[]) : [];
    },
    [form],
  );

  const handleAddSubmit = useCallback(
    async (person: EntityRepresentativesPerson) => {
      form.change(PEOPLE_FIELD_NAME, [...readCurrentPeople(), person]);
      setAddOpen(false);
      setAddError(null);
    },
    [form, readCurrentPeople],
  );

  const editingDisabled = busyAction !== null;

  const handleRemove = useCallback(
    (row: EntityRepresentativeReviewRow) => {
      if (editingDisabled) return;

      const next = readCurrentPeople().map((person) =>
        person.id === row.id ? { ...person, removed: true } : person,
      );
      form.change(PEOPLE_FIELD_NAME, next);
    },
    [editingDisabled, form, readCurrentPeople],
  );

  const handleAddClose = useCallback(() => {
    if (editingDisabled) return;
    setAddOpen(false);
    setAddError(null);
  }, [editingDisabled]);

  const handleAddOpen = useCallback(() => {
    if (editingDisabled) return;
    setAddError(null);
    setAddOpen(true);
  }, [editingDisabled]);

  const renderRow = useCallback(
    (row: EntityRepresentativeReviewRow) => {
      const linked = getLinkedVrStatus(row, linksByVrId);
      const roleChips = getRoleChips(row);
      const canCopyLink = !row.isRemoved && !!row.childVrId;
      const canSendEmail = canCopyLink && row.childVrHasEmail;
      const showContact = row.isSelfService && row.contact.length > 0;
      const canRemove = canEdit && !row.isRemoved;

      return (
        <Stack
          key={row.id}
          spacing={0.5}
          sx={{
            marginBottom: 4,
            opacity: row.isRemoved ? 0.65 : 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            {row.isRemoved && (
              <Chip
                label="Removed"
                size="small"
                color="default"
                variant="outlined"
              />
            )}
            <Typography>{row.fullName}</Typography>
            {showContact && (
              <Typography variant="body2" color="text.secondary">
                {row.contact}
              </Typography>
            )}
            {roleChips.length > 0 && roleChips}
            {row.childVrId && (
              <VerificationStatusBadge
                status={linked?.status}
                statusesConfig={statusesConfig}
                size="small"
              />
            )}
          </Stack>

          {row.childVrId ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              sx={{ marginTop: 0.5 }}
            >
              <Link
                href={`/verification-requests/${row.childVrId}${location.search}`}
                underline="hover"
                variant="caption"
              >
                OPEN
              </Link>
              {canCopyLink && (
                <Button
                  size="small"
                  variant="text"
                  sx={ACTION_BUTTON_SX}
                  disabled={editingDisabled}
                  onClick={() => handleCopyLink(row.childVrId!)}
                >
                  Copy link
                </Button>
              )}
              {canSendEmail && (
                <Button
                  size="small"
                  variant="text"
                  sx={ACTION_BUTTON_SX}
                  disabled={editingDisabled}
                  onClick={() => handleSendInvite(row.childVrId!)}
                >
                  Send email
                </Button>
              )}
              {canRemove && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  sx={ACTION_BUTTON_SX}
                  disabled={editingDisabled}
                  onClick={() => handleRemove(row)}
                >
                  Remove
                </Button>
              )}
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              sx={{ marginTop: 0.5 }}
            >
              {!row.isRemoved && (
                <Typography variant="caption" color="text.secondary">
                  No linked verification request
                </Typography>
              )}
              {canRemove && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  sx={ACTION_BUTTON_SX}
                  disabled={editingDisabled}
                  onClick={() => handleRemove(row)}
                >
                  Remove
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      );
    },
    [
      canEdit,
      editingDisabled,
      handleCopyLink,
      handleRemove,
      handleSendInvite,
      linksByVrId,
      location.search,
      statusesConfig,
    ],
  );

  return (
    <>
      {activeRows.length === 0 && removedRows.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ marginBottom: 1 }}
        >
          No representatives to display.
        </Typography>
      )}

      {activeRows.map(renderRow)}

      {activeRows.length === 0 && removedRows.length > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ marginBottom: 1 }}
        >
          No active representatives to display.
        </Typography>
      )}

      {removedRows.length > 0 && (
        <Box sx={{ marginBottom: 1 }}>
          <Button
            size="small"
            variant="text"
            sx={ACTION_BUTTON_SX}
            disabled={editingDisabled}
            onClick={() => setShowRemoved((current) => !current)}
          >
            {showRemoved
              ? `Hide removed (${removedRows.length})`
              : `Show removed (${removedRows.length})`}
          </Button>
          {showRemoved && (
            <Box sx={{ marginTop: 1 }}>{removedRows.map(renderRow)}</Box>
          )}
        </Box>
      )}

      {shouldShowSendAll && (
        <Box sx={{ marginBottom: 1 }}>
          <Button
            size="small"
            variant="text"
            sx={ACTION_BUTTON_SX}
            disabled={editingDisabled}
            onClick={handleSendAll}
          >
            Send emails to all
          </Button>
        </Box>
      )}

      {canEdit && (
        <Box sx={{ marginTop: 2, marginBottom: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={editingDisabled}
            onClick={handleAddOpen}
          >
            Add Person
          </Button>
        </Box>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ marginBottom: 1 }}>
          {errorMessage}
        </Alert>
      )}

      {canEdit && (
        <>
          <AddPersonDialog
            open={addOpen}
            busy={false}
            errorMessage={addError}
            onClose={handleAddClose}
            onSubmit={handleAddSubmit}
          />
        </>
      )}
    </>
  );
}

// getEntityRepresentativeRows wants a TDocumentData-like binary entry. We
// wrap the current RFF people[] draft in that shape so the row-derivation
// logic stays unchanged when the source moves from mainEntry to form state.
function peopleAsBinaryEntry(people: unknown) {
  const safe = Array.isArray(people) ? people : [];
  return { data: { people: safe } } as unknown as Parameters<
    typeof getEntityRepresentativeRows
  >[0];
}
